/**
 * `/reset-password?token=…` — the second half of the reset, reached from the
 * emailed link.
 *
 * Success sends the user to the login form rather than straight into the app.
 * The worker invalidates every other session when a password changes, so
 * signing in again is both the honest next step and proof the new password
 * works, while they still remember what they typed.
 */

import { MIN_PASSWORD_LENGTH } from "@bigbear/shared";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useSession } from "../../auth/AuthProvider";
import { AuthError } from "../../auth/authApi";
import { AuthCard, Field, firstMissing } from "./AuthCard";

export function ResetPassword() {
  const { api, clear } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !token) return;

    const missing = firstMissing([{ label: "New password", value: password }]);
    if (missing) {
      setError(missing);
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.resetPassword({ token, password });

      // Forget the session before leaving. Changing a password invalidates the
      // sessions that were open against the old one, so keeping the cached user
      // would be a lie — and a concrete one: the login page sends an
      // already-signed-in visitor to the dashboard, so without this the
      // confirmation is never seen and the reset appears to have done nothing.
      clear();
      navigate("/login", {
        replace: true,
        state: { notice: "Your password has been changed. Sign in with the new one." },
      });
    } catch (cause) {
      setError(explain(cause));
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthCard
        title="That link didn't work"
        error="That reset link is missing its code. Copy the whole link from the email, or ask for a new one."
        footer={
          <>
            <Link to="/forgot-password">Send another link</Link>.
          </>
        }
      >
        <p className="muted">Reset links expire an hour after they're sent.</p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Choose a new password"
      error={error}
      footer={
        <>
          Changed your mind? <Link to="/login">Sign in</Link> with your old password.
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        <Field
          id="password"
          label="New password"
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={setPassword}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        />
        <button type="submit" className="button button--primary auth__submit" disabled={busy}>
          {busy ? "Saving…" : "Set new password"}
        </button>
      </form>
    </AuthCard>
  );
}

function explain(cause: unknown): string {
  if (!(cause instanceof AuthError)) {
    return "Could not reach the server. Please try again.";
  }
  if (cause.code === "TOKEN_EXPIRED" || cause.code === "INVALID_TOKEN") {
    return "That reset link has expired or has already been used. Ask for a new one.";
  }
  return cause.message;
}
